<template>
  <div>
    <!-- Loading -->
    <div v-if="loading">
      <img class="logo" src="@/assets/lek/lek-logo-1280x1280.png" alt="LEK Logo" />
      <img class="spinner" src="/image/spinner.gif" />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="bind-success">
      <div class="banner">
        <img src="@/assets/lundbeck/error.png" alt="error" />
        <div class="success-title">出错啦</div>
      </div>
      <img class="logo" src="@/assets/lek/lek-logo-1280x1280.png" alt="LEK Logo" />
      <div class="success-text">{{ msg }}</div>
    </div>

    <!-- Survey -->
    <div v-else-if="showSurvey">
      <public-questionnaire
        :content-id="surveyContentId"
        :open-id="open_id"
        :physician="openPhysician"
        :decorated="true"
        @loaded="onSurveyLoaded"
        @submitted="onSurveySubmitted"
        @error="onSurveyError"
        @empty="onSurveyEmpty"
      />
    </div>

    <!-- Success -->
    <div v-else-if="showSuccess" class="bind-success">
      <div class="banner">
        <img src="@/assets/lundbeck/Success.jpg" alt="success" />
        <div class="success-title">提交成功</div>
      </div>
      <img class="logo" src="@/assets/lek/lek-logo-1280x1280.png" alt="LEK Logo" />
      <div class="success-text">感谢您的参与！</div>
    </div>
  </div>
</template>

<script>
import { getWeChatConfig, getWeChatInfoByCode } from '@/api/wechat';
import {
  getLogContentSend,
  logContentOpen,
  logContentOpenDuration,
} from '@/api/content';
import { getPhysicianWechatDetailByOpenId } from '@/api/content';
import { getLocalStorage, setLocalStorage } from '@/utils';
import { GetRegister } from '@/api/live';
import PublicQuestionnaire from './components/PublicQuestionnaire.vue';

export default {
  name: 'PublicSurvey',
  components: {
    PublicQuestionnaire,
  },
  data() {
    return {
      // verify
      open_id: '',
      send_id: '',
      content_id: '', // 新增：支持通过 URL 传入 content_id
      loading: true,
      error: false,
      msg: '对不起，验证失败',
      contentSend: null,
      content: null,
      openPhysician: null,
      logOpenId: '',
      intervalId: null,
      ticks: 0,

      showSurvey: false,
      surveyTitle: '',
      showSuccess: false,
    };
  },
  computed: {
    surveyContentId() {
      return this.content_id || (this.contentSend && this.contentSend.content_id) || '';
    },
  },
  async created() {
    document.title = this.surveyTitle;

    const urlParams = new URLSearchParams(window.location.search);
    this.send_id = urlParams.get('send_id');
    this.content_id = urlParams.get('cid') || ''; // 允许直接传入 content_id
    let code = urlParams.get('code');

    this.open_id = getLocalStorage('open_id');

    if (code) {
      try {
        let res = await getWeChatInfoByCode(code);
        // Fake open_id
        if (code === 'test0123') {
          res = {
            data: { user: { openid: 'fake_openid_0001' }, token: 'fake_token', seed: 'fake_seed' },
            isSuccess: true,
            message: '',
          };
        }
        if (res?.data?.token) {
          setLocalStorage('token', res.data.token);
          setLocalStorage('seed', res.data.seed);
        }
        if (res?.data?.user?.openid) {
          this.open_id = res.data.user.openid;
          setLocalStorage('open_id', this.open_id);
        } else {
          // 获取失败，进入授权
          return this.authorize();
        }
      } catch (e) {
        // 失败直接授权
        return this.authorize();
      }
    }

    if (this.open_id) {
      try {
        // 先查已绑定医生
        const wxRes = await getPhysicianWechatDetailByOpenId(this.open_id);
        if (wxRes?.data?.physician_id) {
          this.openPhysician = wxRes.data;
        } else {
          // 查外部注册
          const extRes = await GetRegister(this.open_id);
          if (extRes?.data) {
            // 最少需要 open_id/physician_name，便于展示与提交
            this.openPhysician = {
              physician_id: null,
              physician_name: extRes.data.physician_name,
              mobile_phone: extRes.data.mobile_phone || '',
              open_id: this.open_id,
              union_id: null,
            };
          } else {
            // 未注册，跳转注册
            window.location.href = `/public/register${this.send_id ? ('?send_id=' + this.send_id) : ''}`;
            return;
          }
        }
      } catch (e) {
        if (e && e.response && e.response.status === 401) {
          return this.authorize();
        }
        // 其他错误也进入授权兜底
        return this.authorize();
      }

      // 验证完成，加载问卷
      this.loadSurvey();
    } else {
      // 无 open_id，授权
      this.authorize();
    }
  },
  beforeDestroy() {
    if (this.intervalId) clearInterval(this.intervalId);
  },
  unmounted() {
    if (this.intervalId) clearInterval(this.intervalId);
  },
  methods: {
    authorize() {
      getWeChatConfig().then((res) => {
        const appid = res?.data?.appid;
        const redirect_uri = encodeURIComponent(
          window.location.origin + '/public/authorize' + (this.send_id ? '?send_id=' + this.send_id : '')
        );
        window.location.href =
          `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appid}&redirect_uri=${redirect_uri}` +
          `&response_type=code&scope=snsapi_base&state=#wechat_redirect`;
      });
    },
    async loadSurvey() {
      // 拉取 send 记录（用于拿 content_id 打点），失败不阻断
      try {
        if (this.send_id) {
          const r = await getLogContentSend(this.send_id);
          this.contentSend = r?.data || null;
        }
      } catch (e) {
        console.error('Failed to load send record:', e);
      }

      this.loading = false;
      this.showSurvey = true;

      // 打开日志
      const logOpenParams = {
        log_content_send_id: this.send_id,
        content_id: this.surveyContentId,
        reader_physician_id: this.openPhysician && this.openPhysician.physician_id,
        reader_openid: (this.openPhysician && this.openPhysician.open_id) || this.open_id,
        reader_unionid: this.openPhysician && this.openPhysician.union_id,
      };
      logContentOpen(logOpenParams).then((res) => {
        if (res && res.data) this.logOpenId = res.data;
      });

      // 时长日志
      if (this.intervalId) clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        this.ticks++;
        if (this.ticks > (10 * 60) / 5) {
          if (this.ticks % 12 !== 0) return; // 10分钟后每分钟打点
        }
        if (this.logOpenId) logContentOpenDuration({ log_content_open_id: this.logOpenId });
      }, 5000);
    },
    onSurveyLoaded({ title }) {
      this.surveyTitle = title || this.surveyTitle;
      if (this.surveyTitle) document.title = this.surveyTitle;
    },
    onSurveySubmitted() {
      this.showSurvey = false;
      this.showSuccess = true;
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    onSurveyError() {
      this.error = true;
      this.msg = '问卷加载失败';
      this.showSurvey = false;
    },
    onSurveyEmpty() {
      this.error = true;
      this.msg = '问卷不存在或暂无题目';
      this.showSurvey = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.logo {
  display: block;
  width: 60px;
  height: 60px;
  margin: 40px auto;
}
.spinner {
  display: block;
  width: 24px;
  height: 24px;
  margin: 60px auto 120px auto;
}
.bind-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  .banner {
    background-color: #b92504;
    width: 100%;
    height: 280px;
    text-align: center;
  }
  img {
    width: 85px;
    height: 80px;
    margin-top: 80px;
  }
  .success-title {
    font-size: 18px;
    color: #fff;
    margin-top: 10px;
    margin-bottom: 40px;
  }
  .logo {
    width: 60px;
    height: 60px;
    margin: 40px 0;
  }
  .success-text {
    font-size: 16px;
    color: #333;
    line-height: 1.6;
    margin: 0;
  }
}
</style>
